import {useEffect, useMemo, useState} from "react";
import {FiCalendar, FiClock, FiEdit3, FiPercent, FiX} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import Select from "../../../components/common/select/Select";
import styles from "./ExamFormModal.module.css";
import { formatMinutesAsHours, MAX_EXAM_DURATION_MINUTES } from "../../../utils/duration";
import {getExamBaseStudentNumbers, getExamBaseStudents} from "../../../services/examBasesApi";

// The eligible-student list is paged from the server as it is scrolled.
const STUDENT_PAGE_SIZE = 20;

const ExamFormModal = ({
                           isOpen,
                           mode,
                           values,
                           onChange,
                           onClose,
                           onSubmit,
                           saving,
                           error,
                           examBases,
                           examBasesLoading,
                           onToggleStudent,
                           onSelectAll,
                           startTimeRef,
                       }) => {
    const { t } = useTranslation();
    const [studentSearch, setStudentSearch] = useState("");
    /** Shown when submit ran with no exam base picked — replaces the native `required` bubble. */
    const [baseMissing, setBaseMissing] = useState(false);

    const isEdit = mode === "edit";
    const title = isEdit ? t("pages.examForm.editTitle") : t("pages.examForm.createTitle");

    const selectedNumbers = useMemo(() => {
        return values.assignedStudentNumbers
            ? values.assignedStudentNumbers
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [];
    }, [values.assignedStudentNumbers]);

    // Eligible students, paged and searched server-side. The modal used to wait on one request that
    // returned every enrolled student of every topic before it could render the list.
    const [students, setStudents] = useState([]);
    const [studentsPage, setStudentsPage] = useState(0);
    const [studentsTotal, setStudentsTotal] = useState(0);
    const [studentsHasMore, setStudentsHasMore] = useState(false);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [studentsError, setStudentsError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectAllBusy, setSelectAllBusy] = useState(false);

    const examBaseId = values.examBaseId;

    // Debounce the search box so typing doesn't fire a request per keystroke.
    useEffect(() => {
        const id = setTimeout(() => setSearchTerm(studentSearch.trim()), 350);
        return () => clearTimeout(id);
    }, [studentSearch]);

    // Reset whenever the modal closes or the chosen template changes, so a stale roster is never
    // shown against a different template.
    useEffect(() => {
        setStudents([]);
        setStudentsPage(0);
        setStudentsTotal(0);
        setStudentsHasMore(false);
        setStudentsError("");
    }, [examBaseId, isOpen]);

    useEffect(() => {
        if (!isOpen || !examBaseId) return;
        let cancelled = false;
        const load = async () => {
            setStudentsLoading(true);
            setStudentsError("");
            try {
                const data = await getExamBaseStudents(Number(examBaseId), {
                    pageNo: studentsPage + 1,
                    pageSize: STUDENT_PAGE_SIZE,
                    search: searchTerm,
                });
                if (cancelled) return;
                const rows = Array.isArray(data?.content) ? data.content : [];
                const total = Number(data?.totalElements) || 0;
                setStudentsTotal(total);
                setStudents((prev) => {
                    const next = studentsPage === 0 ? rows : [...prev, ...rows];
                    setStudentsHasMore(next.length < total);
                    return next;
                });
            } catch (err) {
                if (cancelled) return;
                setStudentsHasMore(false);
                setStudentsError(
                    err?.response?.data?.message || err?.response?.data?.error || t("pages.examForm.noStudentsFound")
                );
            } finally {
                if (!cancelled) setStudentsLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [isOpen, examBaseId, studentsPage, searchTerm, t]);

    // A new search term restarts at page 0.
    useEffect(() => {
        setStudentsPage(0);
    }, [searchTerm]);

    // Infinite scroll: pull the next page as the list nears its bottom.
    const handleStudentsScroll = (e) => {
        if (studentsLoading || !studentsHasMore) return;
        const {scrollTop, scrollHeight, clientHeight} = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 80) {
            setStudentsPage((p) => p + 1);
        }
    };

    // Names of the loaded rows, so a selected student who has scrolled or searched out of view still
    // shows something readable on their chip.
    const nameByNumber = useMemo(() => {
        const map = new Map();
        students.forEach((s) => {
            map.set(String(s.studentNumber), `${s.firstName || ""} ${s.lastName || ""}`.trim());
        });
        return map;
    }, [students]);

    // "Select all matching" asks the server for every matching number, not just the pages scrolled
    // so far, so it means the same thing it did when the modal loaded the whole roster.
    const handleSelectAllMatching = async () => {
        if (selectAllBusy || !examBaseId) return;
        setSelectAllBusy(true);
        try {
            const numbers = await getExamBaseStudentNumbers(Number(examBaseId), {search: searchTerm});
            onSelectAll && onSelectAll(Array.isArray(numbers) ? numbers : [], true);
        } catch (err) {
            setStudentsError(err?.response?.data?.message || err?.message || "");
        } finally {
            setSelectAllBusy(false);
        }
    };

    const durationExceedsMax = Number(values.durationMinutes) > MAX_EXAM_DURATION_MINUTES;

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true"
             aria-labelledby="exam-form-modal-title">
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <div className={styles.titleWrap}>
                        <span className={styles.headerIcon}>{isEdit ? <FiEdit3 size={22}/> :
                            <FiCalendar size={22}/>}</span>
                        <h2 id="exam-form-modal-title" className={styles.title}>{title}</h2>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label={t("pages.examForm.closeAria")}>
                        <FiX size={20}/>
                    </button>
                </div>

                {/* The old native select carried `required`; a custom control gets no native
                    constraint validation, so the exam-base check runs here before delegating. */}
                <form
                    onSubmit={(e) => {
                        if (!values.examBaseId) {
                            e.preventDefault();
                            setBaseMissing(true);
                            return;
                        }
                        setBaseMissing(false);
                        onSubmit(e);
                    }}
                    className={styles.form}
                >
                    <div className={styles.formBody}>
                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t("pages.examForm.selectExam")}</label>
                            <Select
                                name="examBaseId"
                                value={values.examBaseId}
                                onChange={(e) => {
                                    setBaseMissing(false);
                                    onChange(e);
                                }}
                                className={styles.select}
                                disabled={saving}
                                options={[
                                    {
                                        value: "",
                                        label: examBasesLoading
                                            ? t("pages.examForm.examLoading")
                                            : t("pages.examForm.examPlaceholder"),
                                    },
                                    ...(Array.isArray(examBases) ? examBases : []).map((base) => ({
                                        value: base.id,
                                        label: base.title,
                                    })),
                                ]}
                            />
                            {baseMissing && (
                                <div className={styles.error} role="alert">
                                    {t("pages.examForm.examRequired", {
                                        defaultValue: "İmtahan şablonu seçilməlidir",
                                    })}
                                </div>
                            )}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t("pages.examForm.startDateTime")}</label>
                            <input
                                ref={startTimeRef}
                                type="datetime-local"
                                name="scheduledStartTime"
                                value={values.scheduledStartTime}
                                onChange={onChange}
                                className={styles.input}
                                required
                                disabled={saving}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>
                                <FiClock size={14} className={styles.labelIcon}/>
                                {t("pages.examForm.duration")}
                            </label>
                            <input
                                type="number"
                                name="durationMinutes"
                                value={values.durationMinutes}
                                onChange={onChange}
                                className={styles.input}
                                placeholder={t("pages.examForm.durationPlaceholder")}
                                min={1}
                                max={300}
                            />
                            {durationExceedsMax ? (
                                <span style={{display: "block", marginTop: 4, fontSize: 12, color: "#dc2626"}}>
                                    {t("common.durationMax")}
                                </span>
                            ) : formatMinutesAsHours(values.durationMinutes, t) ? (
                                <span style={{display: "block", marginTop: 4, fontSize: 12, color: "#6b7280"}}>
                                    = {formatMinutesAsHours(values.durationMinutes, t)}
                                </span>
                            ) : null}
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>
                                <FiPercent size={14} className={styles.labelIcon}/>
                                {t("pages.examForm.passRate")}
                            </label>
                            <input
                                type="number"
                                name="passRate"
                                value={values.passRate}
                                onChange={onChange}
                                className={styles.input}
                                placeholder={t("pages.examForm.passRatePlaceholder")}
                                min={1}
                                max={100}
                                step={1}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t("pages.examForm.proctored")}</label>
                            <div className={styles.checkboxRow}>
                                <input
                                    type="checkbox"
                                    name="proctored"
                                    checked={values.proctored === true}
                                    onChange={onChange}
                                    disabled={saving}
                                    className={styles.checkbox}
                                />
                                <span className={styles.checkboxText}>{t("pages.examForm.activate")}</span>
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t("pages.examForm.note")}</label>
                            <textarea
                                name="note"
                                value={values.note}
                                onChange={onChange}
                                className={styles.textarea}
                                rows={3}
                                placeholder={t("pages.examForm.notePlaceholder")}
                                disabled={saving}
                            />
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.label}>{t("pages.examForm.studentNumbers")}</label>
                            <input
                                type="text"
                                name="assignedStudentNumbers"
                                value={values.assignedStudentNumbers}
                                disabled
                                className={styles.input}
                                placeholder={t("pages.examForm.studentNumbersPlaceholder")}
                            />
                        </div>

                        {values.examBaseId && (
                            <div className={styles.studentsBox}>
                                <div className={styles.studentsBoxHeader}>
                                    <span className={styles.studentsBoxTitle}>{t("pages.examForm.studentsForExam")}</span>
                                    <span className={styles.studentsBoxHeaderRight}>
                                        {/* Eligible total for the current search — not the loaded page size. */}
                                        <span className={styles.studentsBoxCount}>{t("pages.examForm.studentsCount", { count: studentsTotal })}</span>
                                        {studentsTotal > 0 && (
                                            <button
                                                type="button"
                                                className={styles.selectAllBtn}
                                                onClick={handleSelectAllMatching}
                                                disabled={saving || selectAllBusy}
                                            >
                                                {t("pages.examForm.selectAll")}
                                            </button>
                                        )}
                                        {selectedNumbers.length > 0 && (
                                            <button
                                                type="button"
                                                className={styles.selectAllBtn}
                                                onClick={() => onSelectAll && onSelectAll(selectedNumbers, false)}
                                                disabled={saving}
                                            >
                                                {t("pages.examForm.deselectAll")}
                                            </button>
                                        )}
                                    </span>
                                </div>

                                {/* Chosen students stay pinned here, so a search or scroll can never
                                    hide what is already selected. */}
                                {selectedNumbers.length > 0 && (
                                    <div className={styles.selectedChips}>
                                        {selectedNumbers.map((sn) => (
                                            <span key={sn} className={styles.selectedChip}>
                                                {nameByNumber.get(sn) || sn}
                                                <button
                                                    type="button"
                                                    className={styles.selectedChipRemove}
                                                    onClick={() => onToggleStudent && onToggleStudent(sn)}
                                                    disabled={saving}
                                                    aria-label={t("pages.examForm.deselectAll")}
                                                >
                                                    <FiX size={12}/>
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <input
                                    type="text"
                                    value={studentSearch}
                                    onChange={(e) => setStudentSearch(e.target.value)}
                                    className={styles.searchInput}
                                    placeholder={t("pages.examForm.studentSearch")}
                                    disabled={saving}
                                />

                                {studentsError ? (
                                    <div className={styles.infoText}>{studentsError}</div>
                                ) : students.length === 0 && studentsLoading ? (
                                    <div className={styles.infoText}>{t("pages.examForm.studentsLoading")}</div>
                                ) : students.length === 0 ? (
                                    <div className={styles.infoText}>{t("pages.examForm.noStudentsFound")}</div>
                                ) : (
                                    <div className={styles.studentsList} onScroll={handleStudentsScroll}>
                                        {students.map((student) => {
                                            const sn = String(student.studentNumber);
                                            return (
                                                <label key={sn} className={styles.studentRow}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedNumbers.includes(sn)}
                                                        onChange={() => onToggleStudent && onToggleStudent(sn)}
                                                        disabled={saving}
                                                        className={styles.checkbox}
                                                    />
                                                    <span className={styles.studentText}>
                                                        {sn} – {student.firstName} {student.lastName}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                        {studentsLoading && (
                                            <div className={styles.infoText}>{t("pages.examForm.studentsLoading")}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {error && (
                            <div className={styles.error} role="alert">
                                {error}
                            </div>
                        )}
                    </div>

                    <div className={styles.actions}>
                        <button type="button" onClick={onClose} className={styles.secondaryBtn} disabled={saving}>
                            {t("common.cancel")}
                        </button>
                        <button type="submit" disabled={saving || durationExceedsMax} className={styles.primaryBtn}>
                            {saving ? t("pages.examForm.saving") : isEdit ? t("pages.examForm.saveBtn") : t("pages.examForm.addBtn")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ExamFormModal;
